import Skeleton from '@carvana/ds-skeleton';
import { SearchSource } from '@carvana/merch-contracts';
import cx from 'classnames';
import { useContext, useEffect, useRef } from 'react';
import Chip from '@/common/Chip';
import { useTrackEvent } from '~/context/common/AnalyticsProvider';
import AppliedFiltersContext from '~/context/common/AppliedFiltersContext';
import SearchEventsDataContext from '~/context/srp/SearchEventsDataContext';
import SuggestedFiltersContext from '~/context/srp/SuggestedFiltersContext';
import type { SuggestedFilter } from '~/contracts/srp';
import {
  useIsSuggestedFiltersLoaded,
  useShowSuggestedFilters
} from '~/hooks/srp/selectors/useSuggestedFilters';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';
import toLowerKebabCase from '~/utilities/toLowerKebabCase';
import { suggestedFiltersSkeletons } from './constants';
import {
  containerNegativeMargin,
  containerPadding
} from '../MainLayout.styles';

type OwnProps = {
  className?: string;
};

function QuickFilters({ className }: OwnProps) {
  const { setAppliedFilters } = useContext(AppliedFiltersContext);
  const { setSearchSource } = useContext(SearchEventsDataContext);
  const { suggestedFilters, suggestedFiltersRequestId } = useContext(
    SuggestedFiltersContext
  );
  const scrollRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isSuggestedFiltersLoaded = useIsSuggestedFiltersLoaded();
  const showSuggestedFilters = useShowSuggestedFilters();
  const trackEvent = useTrackEvent();

  const handleSuggestedFilterClick = (suggestedFilter: SuggestedFilter) => {
    setSearchSource(SearchSource.SuggestedFilter);
    setAppliedFilters(suggestedFilter.searchFilters);

    trackEvent('Suggested filters', {
      action: 'clicked',
      suggestedFilter,
      suggestedFiltersRequestId
    });
  };

  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollLeft = 0;
  }, [scrollRef, suggestedFilters.isLoading]);

  return showSuggestedFilters ? (
    <>
      <div
        className={cx(
          className,
          containerNegativeMargin,
          'relative mt-xl flex min-h-24 items-center overflow-hidden sm:mt-xl',
          {
            hidden:
              isSuggestedFiltersLoaded && suggestedFilters.data.length === 0
          }
        )}
        data-testid="srp-quick-filters"
        ref={containerRef}
      >
        <div
          className={cx(containerPadding, 'no-scrollbar flex overflow-x-auto')}
          ref={scrollRef}
        >
          <div className="flex items-center gap-md sm:flex-wrap ">
            {isSuggestedFiltersLoaded
              ? suggestedFilters.data.map((suggestedFilter, i) => (
                  <Chip
                    key={`${suggestedFilter.displayName}-${i}`}
                    className="items-baseline px-lg t-label-sm"
                    data-adobe-id={toLowerKebabCase(
                      'suggested-filters',
                      suggestedFilter.displayName
                    )}
                    data-analytics-id={null}
                    data-testid="suggested-filter"
                    testId="suggested-filter"
                    onClick={() => handleSuggestedFilterClick(suggestedFilter)}
                  >
                    {suggestedFilter.displayName}
                    <span className="text-text-weak t-body-xs">
                      ({suggestedFilter.totalMatchedInventory.toLocaleString()})
                    </span>
                  </Chip>
                ))
              : suggestedFiltersSkeletons.map((width, index) => (
                  <Skeleton
                    key={index}
                    height="34px"
                    width={`${width}px`}
                    className="shrink-0 rounded-md py-[7px] t-body-sm"
                    data-testid="suggested-filter-skeleton"
                  >
                    &nbsp;
                  </Skeleton>
                ))}
          </div>
        </div>
      </div>
    </>
  ) : null;
}

export default withErrorBoundary(QuickFilters);
